import {
  FAMILY,
  HTML_PAGE_LOCAL_FAMILY_MAX_CHARS,
  LOCAL_FONT_FAMILY_MAX_COUNT,
} from '@app/shared'
import type {
  FontReference,
  FontSource,
  LocalFacesInput,
} from '@segnavia/format'
import { localFontReferences } from '@segnavia/format'
import { type LocalFonts, readLocalFonts } from '@segnavia/frame/local-fonts'
import { useEffect, useState } from 'react'
import type { DocHostClient, DocRef } from './DocView'
import { setLocalFontSetting, useLocalFontSetting } from './local-font-setting'

export const pageFontSource: FontSource = {
  async font(name) {
    if (!(name in FAMILY)) return undefined
    return {
      family: FAMILY[name as keyof typeof FAMILY],
      // The projected page already carries the app faces. Segnavia supplies
      // their custom-property names here and adds only browser-local bytes.
      faces: [],
    }
  },
}

function unavailable(
  references: readonly FontReference[],
  reason: 'unsupported' | 'denied',
): LocalFonts {
  return {
    faces: [],
    unavailable: localFontReferences(references).map((reference) => ({
      family: reference.local,
      fallback: reference.fallback,
      reason,
    })),
  }
}

/** Read only the faces a page names, prompting through Segnavia when needed. */
export async function readPageLocalFonts(
  references: readonly FontReference[],
  signal: AbortSignal,
): Promise<LocalFonts> {
  if (localFontReferences(references).length === 0) {
    return { faces: [], unavailable: [] }
  }
  return readLocalFonts(references, { signal })
}

/** Whether this browser can share the fonts installed on its computer at all. */
export function localFontsSupported(): boolean {
  return (
    typeof (globalThis as { queryLocalFonts?: unknown }).queryLocalFonts ===
    'function'
  )
}

/**
 * Whether a page may read this browser's fonts, and if not, what would change
 * that: `prompt` is one click from yes, `denied` is the browser's own site
 * settings, `off` is the reader's own setting, `unsupported` is nothing.
 */
export type LocalFontAccess =
  | 'granted'
  | 'prompt'
  | 'denied'
  | 'off'
  | 'unsupported'

/**
 * What a reader is told where the browser itself stands in the way, said the
 * same wherever the access is shown.
 */
export const LOCAL_FONT_ACCESS_NOTE: Partial<Record<LocalFontAccess, string>> =
  {
    denied:
      'This browser blocks the site from reading your fonts. Allow fonts in its site settings to use them.',
    unsupported:
      'This browser does not share the fonts installed on a computer, so a page prints in the fallbacks it names.',
  }

/**
 * The access as it stands, followed as it changes — a yes or no at the prompt,
 * or the browser's own site settings, re-reads without the page reloading.
 */
export function useLocalFontAccess(): LocalFontAccess | null {
  const setting = useLocalFontSetting()
  const [state, setState] = useState<PermissionState | 'unsupported' | null>(
    null,
  )
  useEffect(() => {
    if (!localFontsSupported() || !navigator.permissions) {
      setState('unsupported')
      return
    }
    let status: PermissionStatus | null = null
    let current = true
    const update = () => {
      if (current && status) setState(status.state)
    }
    // TypeScript's web library has not yet added Chromium's local-fonts name.
    navigator.permissions.query({ name: 'local-fonts' as PermissionName }).then(
      (answer) => {
        // An answer arriving after the reader left has nobody to follow it for.
        if (!current) return
        status = answer
        answer.addEventListener('change', update)
        update()
      },
      () => current && setState('unsupported'),
    )
    return () => {
      current = false
      status?.removeEventListener('change', update)
    }
  }, [])
  // Null while the browser has not answered, so a reader with the access is
  // not told for a moment that the face is unavailable.
  if (state === null || state === 'unsupported') return state
  return setting === 'off' ? 'off' : state
}

/**
 * Read for preview only when the access already stands. The browser shows its
 * prompt only straight after a click, so a page opening never asks.
 */
export async function readGrantedPageLocalFonts(
  references: readonly FontReference[],
  access: LocalFontAccess,
  signal: AbortSignal,
): Promise<LocalFonts> {
  if (localFontReferences(references).length === 0) {
    return { faces: [], unavailable: [] }
  }
  if (access !== 'granted') {
    return unavailable(
      references,
      access === 'unsupported' ? 'unsupported' : 'denied',
    )
  }
  return readPageLocalFonts(references, signal)
}

/**
 * Ask the browser, from the click that offers it. Turning the setting on is
 * the same act, so a reader who says yes here is not asked again on the next
 * page.
 */
export async function askForLocalFonts(): Promise<boolean> {
  if (!localFontsSupported()) return false
  try {
    await (
      globalThis as unknown as { queryLocalFonts: () => Promise<unknown[]> }
    ).queryLocalFonts()
  } catch {
    return false
  }
  setLocalFontSetting('on')
  return true
}

type FamilyQuery = () => Promise<readonly { family: string }[]>

function queryInstalledFamilies(): Promise<readonly { family: string }[]> {
  return (
    globalThis as unknown as { queryLocalFonts: FamilyQuery }
  ).queryLocalFonts()
}

/**
 * The family names this computer holds, for the assistant writing a page —
 * read only where the access already stands, so asking for help never raises
 * the browser's prompt. A name the server would refuse is left out here
 * rather than refusing the whole turn.
 */
export async function localFontFamiliesFor(
  access: LocalFontAccess | null,
  query: FamilyQuery = queryInstalledFamilies,
): Promise<string[] | undefined> {
  if (access !== 'granted') return undefined
  let faces: readonly { family: string }[]
  try {
    faces = await query()
  } catch {
    return undefined
  }
  const families = new Set<string>()
  for (const { family } of faces) {
    if (
      family &&
      family.length <= HTML_PAGE_LOCAL_FAMILY_MAX_CHARS &&
      !/\p{Cc}/u.test(family)
    ) {
      families.add(family)
    }
  }
  return [...families]
    .sort((a, b) => a.localeCompare(b))
    .slice(0, LOCAL_FONT_FAMILY_MAX_COUNT)
}

function responseFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1]
  if (utf8) return decodeURIComponent(utf8)
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? fallback
}

/**
 * The print beside a host's own download that carries the reader's faces for
 * one render. It is keyed by the SERVER's page host, which a client registers
 * under a name of its own, so only a host the print route serves opts in.
 */
export function printWithLocalFaces(
  host: string,
): NonNullable<DocHostClient['printWithLocalFaces']> {
  return (doc, faces, fallbackFilename) =>
    postLocalFaces(host, doc, faces, fallbackFilename)
}

async function postLocalFaces(
  host: string,
  doc: DocRef,
  faces: LocalFacesInput,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  const query = new URLSearchParams({
    host,
    spaceId: doc.spaceId,
    key: doc.key,
  })
  const response = await fetch(`/api/pdf/page?${query}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(faces),
  })
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null)
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? Reflect.get(payload, 'error')
        : null
    throw new Error(
      typeof message === 'string' ? message : 'The PDF could not be prepared.',
    )
  }
  return {
    blob: await response.blob(),
    filename: responseFilename(response, fallbackFilename),
  }
}
